import {
  Box,
  Button,
  Grid,
  Stack,
  TextField,
  Typography,
  MenuItem,
  Select,
  OutlinedInput,
  Chip,
  FormControl,
  InputLabel,
  FormHelperText,
  Alert,
} from '@mui/material'
import React, { useEffect, useState } from 'react'
import {
  getUserDetails,
  updateUser,
  removeUser,
  createUser,
  getAllRegions,
} from '../../model'
import { RoleType } from '../ViewModel'

interface UserEditPageProps {
  userId: string
  onClose: () => void
}

/**
 * @summary Controls the rendering of the form for performing CRUD operations on a user
 * @description Controls the addition/updation/deletion of a user
 * @returns JSX.Element
 */
export default function UsersEditPage({ userId, onClose }: UserEditPageProps) {
  const [buttonTitle, setButtonTitle] = useState('')

  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [roleType, setRoleType] = useState<RoleType>(RoleType.staff)
  const [regionsAccessed, setRegionsAccessed] = useState<string[]>([])
  const availableRegions = getAllRegions()

  const [loading, setLoading] = useState(false)

  const [error, setError] = useState(false)

  function isValidId() {
    return userId && userId.length !== 0
  }

  const initializeView = async () => {
    if (isValidId()) {
      const userData = await getUserDetails(userId)
      setName(userData.name)
      setPhone(userData.phone)
      setEmail(userData.email)
      setRoleType(userData.role.type)
      setRegionsAccessed(userData.role.regions_accessed)

      setButtonTitle('Update')
    } else {
      setName('')
      setPhone('')
      setEmail('')
      setPassword('')
      setRoleType(RoleType.staff)
      setRegionsAccessed([])

      setButtonTitle('Create New User')
    }
  }

  useEffect(() => {
    initializeView()

    return () => {
      setError(false)
    }
  }, [])

  const handleRegionsSelection = (e) => {
    const {
      target: { value },
    } = e
    setRegionsAccessed(
      // On autofill we get a stringified value.
      typeof value === 'string' ? value.split(',') : value
    )
    console.log(`Regions Selected: ${regionsAccessed}`)
  }

  const handleSubmit = async () => {
    setLoading(true)
    try {
      if (isValidId()) {
        await updateUser(userId, name, email, phone, roleType, regionsAccessed)
        onClose()
      } else {
        await createUser(
          name,
          phone,
          email,
          password,
          roleType,
          regionsAccessed
        )
        onClose()
      }
    } catch (e) {
      setError(true)
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (event) => {
    event.preventDefault()
    setLoading(true)
    if (isValidId()) {
      try {
        await removeUser({ id: userId, name, phone, email, password })
      } catch (e) {
        console.log('Deleting Error: ' + e)
      }
      onClose()
    }
    setLoading(false)
  }

  return (
    <Stack padding={5}>
      <Typography variant="h3" color="black">
        {name.trim() !== '' ? name : 'New User'}
      </Typography>
      <Box sx={{ mt: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              required
              fullWidth
              label="Name"
              autoFocus
              value={name ?? ''}
              onChange={(e) => {
                setName(e.target.value)
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Phone Number"
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
              value={phone ?? ''}
              onChange={(e) => {
                setPhone(e.target.value)
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              disabled={isValidId()}
              required={!isValidId()}
              fullWidth
              label="Email"
              value={email ?? ''}
              onChange={(e) => {
                setEmail(e.target.value)
              }}
            />
          </Grid>
          {!isValidId() && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Password"
                value={password ?? ''}
                onChange={(e) => {
                  setPassword(e.target.value)
                }}
              />
            </Grid>
          )}
          <Grid item xs={12}>
            <FormControl sx={{ minWidth: 120 }} fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                value={roleType}
                label="Role"
                onChange={(e) => {
                  setRoleType(e.target.value as RoleType)
                }}
              >
                <MenuItem value={RoleType.general_user}>General User</MenuItem>
                <MenuItem value={RoleType.staff}>Staff</MenuItem>
                <MenuItem value={RoleType.leader}>Leader</MenuItem>
                <MenuItem value={RoleType.admin}>Administrator</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl sx={{ minWidth: 120 }} fullWidth>
              <InputLabel>Regions Access</InputLabel>
              <Select
                label="Regions Accessed"
                id="regions_accessed"
                multiple
                value={regionsAccessed}
                onChange={handleRegionsSelection}
                input={<OutlinedInput id="select-multiple-chip" label="Chip" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={value} />
                    ))}
                  </Box>
                )}
              >
                {availableRegions.map((region) => (
                  <MenuItem
                    key={region}
                    value={region}
                    // style={getStyles(name, personName, theme)}
                  >
                    {region}
                  </MenuItem>
                ))}
              </Select>
              {regionsAccessed.length === 0 && (
                <FormHelperText>
                  If no regions are selected, user will have access to all sites
                </FormHelperText>
              )}
            </FormControl>
          </Grid>
        </Grid>
        <Button
          type="submit"
          fullWidth
          variant="contained"
          sx={{ mt: 3, mb: 2 }}
          disabled={loading}
          onClick={handleSubmit}
        >
          {buttonTitle}
        </Button>
        {isValidId() && (
          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="error"
            sx={{ mt: 3, mb: 2 }}
            disabled={loading}
            onClick={handleDelete}
          >
            Delete {name ?? ''}
          </Button>
        )}
      </Box>
      {error && (
        <Alert severity="error">
          Unable to submit form! Please correctly enter fields!
        </Alert>
      )}
    </Stack>
  )
}
